import { useEffect, useState } from 'react';
import { homeTaskApi } from '../api';
import { formateData } from '../components/common/Task/formateData';
import { errorListScroll } from '../helpers/errorListScroll';
import { jsonConvert } from '../helpers/jsonConvert';
import { trimFields } from '@UI/New/Constructor/helpers/trimFields';
import { useAddAlert } from './useAddAlert';
import {
	getProbeTableConfig,
	hasSavedProbeTable,
	isProbeTask,
	isProbeTablePartial,
	normalizeProbeTableAnswer,
	validateProbeTableTask,
} from '../helpers/probeAnswerTable';
import {
	isCodeExecutionEnabled,
	normalizeCodeLanguages,
	normalizeDefaultCodeLanguage,
} from '../components/common/Task/CodeEditor/codeLanguages';
import {
	isCodeInputParametersEnabled,
	normalizeCodeInputParameters,
} from '../components/common/Task/CodeEditor/inputParameters';

const isTrueValue = (value) => value === true || value === 1 || value === '1';
const isProbeEditor = (state) => isProbeTask(state) || window.location.href.includes('/direction-editor/probe-');
const AI_AUTO_CHECK_TASK_TYPES = [
	'detail-answer',
	'file-answer',
	'drawing-image',
	'answer-in-the-code-editor',
];

export const getInitNewTask = (type = null) => ({
	number_exam_id: null,
	topic_id: null,
	subtopic_id: null,
	sub_subtopic_id: null,
	question: '',
	reply_button_text: '',
	type: 'test',
	ai_tutor_id: null,
	images: [],
	images_drawing: [],
	audio: [],
	answer_options: ['pass-words2', 'pass-words-select'].includes(type)? '' : [],
	complexity: null,
	key_text: '',
	key_files: [],
	number_of_attempts: 2,
	is_required: false,
	prompt: '',
	exam_weight: [{ score: 0, description: 'Решено неверно' }, { score: 1, description: 'Решено верно' }],
	cant_upload_files: false,
	can_record_audio_in_response: false,
	is_timed_task: false,
	hint_after_all_attempts: false,
	is_number_answer: false,
	video_solution: '',
	video_solution_platform_media: '',
	video_solution_iframe: '',
	video_source_type: 'youtube',
	show_video_solution: false,
	show_code_input: false,
	can_write_text_in_response: false,
	can_get_code_result: type === 'answer-in-the-code-editor',
	code_languages: type === 'answer-in-the-code-editor' ? ['python', 'pascal', 'java'] : [],
	default_code_language: type === 'answer-in-the-code-editor' ? 'python' : '',
	explain_code_errors_with_ai: false,
	code_input_parameters_enabled: false,
	code_input_parameters: [],
	ai_auto_check_enabled: false,
	ai_auto_check_score_criteria: '',
	ai_auto_check_comment_prompt: '',
});

export const buildTaskFormData = (state) => {
	const data = new FormData();
	const forProbe = isProbeEditor(state);
	if (forProbe && state.type === 'exact-answer') {
		data.append('probe_answer_format', state.probe_answer_format || 'line');
		if (state.probe_answer_format) {
			data.append('probe_answer_table', JSON.stringify(getProbeTableConfig(state)));
			data.append('probe_table_correct_answer', JSON.stringify(normalizeProbeTableAnswer(state.probe_table_correct_answer)));
		}
	}
	data.append('is_probe', forProbe ? 1 : 0)
	data.append('topic_id', state['topic_id']);
	data.append('subtopic_id', state['subtopic_id']);
	data.append('sub_subtopic_id', state['sub_subtopic_id']);
	data.append('type', state['type']);
	data.append('ai_tutor_id', state['ai_tutor_id'] ?? '');
	data.append('number_of_attempts', state['number_of_attempts']);
	data.append('is_required', isTrueValue(state['is_required']) ? 1 : 0);
	data.append('question', jsonConvert(state['question']));
	data.append('reply_button_text', jsonConvert(state['reply_button_text']));
	if(state.type === 'basket'){
		state['answer_options'].forEach((el, index)=>{
			data.append(`answer_options[${index}][id]`, el.id);
			data.append(`answer_options[${index}][type]`, el.type);
			data.append(`answer_options[${index}][text]`, jsonConvert(el.text));
			data.append(`answer_options[${index}][img]`, el.img);
			data.append(`answer_options[${index}][basket_id]`, el.basket_id);
		})
	}
	else{
		data.append('answer_options', jsonConvert(state['answer_options'] ?? [{}]));
	}
	data.append('baskets', jsonConvert(state['baskets'] ?? [{}]));
	data.append('cant_upload_files', state['cant_upload_files']);
	data.append('can_record_audio_in_response', state['can_record_audio_in_response']);
	data.append('is_timed_task', state['is_timed_task']);
	data.append('hint_after_all_attempts', state['hint_after_all_attempts']);
	data.append('is_number_answer', state['is_number_answer']);
	if (state['type'] === 'exact-answer') {
		data.append('show_code_input', isTrueValue(state['show_code_input']) ? 1 : 0);
	}
	data.append('can_write_text_in_response', state['can_write_text_in_response'] ? 1 : 0);
	if (state['type'] === 'answer-in-the-code-editor') {
		const codeLanguages = normalizeCodeLanguages(state['code_languages']);
		const canGetCodeResult = isCodeExecutionEnabled(state['can_get_code_result']);
		data.append('can_get_code_result', canGetCodeResult ? 1 : 0);
		data.append('code_languages', jsonConvert(codeLanguages));
		data.append('default_code_language', normalizeDefaultCodeLanguage(state['default_code_language'], codeLanguages));
		data.append('explain_code_errors_with_ai', canGetCodeResult && isTrueValue(state['explain_code_errors_with_ai']) ? 1 : 0);
		const useCodeInputParameters = canGetCodeResult && isCodeInputParametersEnabled(state['code_input_parameters_enabled']);
		data.append('code_input_parameters_enabled', useCodeInputParameters ? 1 : 0);
		data.append('code_input_parameters', jsonConvert(useCodeInputParameters ? normalizeCodeInputParameters(state['code_input_parameters']) : []));
	}
	if (AI_AUTO_CHECK_TASK_TYPES.includes(state['type'])) {
		const isAiAutoCheckEnabled = isTrueValue(state['ai_auto_check_enabled']);
		data.append('ai_auto_check_enabled', isAiAutoCheckEnabled ? 1 : 0);
		data.append('ai_auto_check_score_criteria', isAiAutoCheckEnabled ? state['ai_auto_check_score_criteria'] ?? '' : '');
		data.append('ai_auto_check_comment_prompt', isAiAutoCheckEnabled ? state['ai_auto_check_comment_prompt'] ?? '' : '');
	}
	if(!!state['ignore_spaces_and_case']) {
		data.append('ignore_spaces_and_case', 1)
	}
	else{
		data.append('ignore_spaces_and_case', 0)
	}

	if (state['complexity']) data.append("complexity", state['complexity'])

	// if (state['video_solution'])
	data.append('video_solution', state['video_solution']);

	data.append('video_solution_platform_media', state['video_solution_platform_media']);
	data.append('video_solution_iframe', state['video_solution_iframe']);
	data.append('video_source_type', state['video_source_type']);
	data.append(
		'show_video_solution',
		state['show_video_solution'] === true || Number(state['show_video_solution']) === 1 ? 1 : 0
	);

	if (state['key_text'])
		data.append('key_text', jsonConvert(state['key_text']));
	if (state['prompt']) data.append('prompt', jsonConvert(state['prompt']));
	if (forProbe && isProbeTablePartial(state))
		data.append('exam_weight', JSON.stringify([{ score: 0, description: '' }, { score: 1, description: '' }, { score: 2, description: '' }]));
	else if (state['exam_weight'])
		data.append('exam_weight', jsonConvert(state['exam_weight']));

	if (typeof state['number_exam_id'] === 'number') data.append("number_exam_id", state['number_exam_id'])
	if (typeof state['direction_id'] === 'number')
		data.append('direction_id', state['direction_id']);

	if(state.type !== 'match') {
		if (typeof state['correct_answer'] === 'number') {
			data.append('correct_answer', state['correct_answer']);
		}
		else if (state['correct_answer'] && Array.isArray(state['correct_answer'])) {
			const processedArray = state['correct_answer'].map((el) => {
				if (el?.type === "constructor" && Array.isArray(el?.text)) {
					return {
						...el,
						text: el.text.map(trimFields)
					};
				}
				return el;
			});

			data.append('correct_answer', jsonConvert(processedArray));
		}
		else if (state['correct_answer']) {
			data.append('correct_answer', jsonConvert(state['correct_answer']));
		}
	}

	if (state['option_one_mismatch_order'] !== undefined)
		data.append(
			'option_one_mismatch_order',
			state['option_one_mismatch_order']
		);
	if (state['option_all_mismatch_order'] !== undefined)
		data.append(
			'option_all_mismatch_order',
			state['option_all_mismatch_order']
		);
	if (state['option_all_mismatch'] === true || state['option_all_mismatch'] === false)
		data.append(
			'option_all_mismatch',
			state['option_all_mismatch'] ? 1 : 0
		);
	if (state['option_order_matters'] !== undefined)
		data.append('option_order_matters', state['option_order_matters']);
	if (
		state['option_strict'] !== undefined ||
		state['option_partial2'] !== undefined ||
		state['option_partial3'] !== undefined ||
		state['option_unordered'] !== undefined ||
		state['type'] === 'order'
	) {
		data.append('option_strict', state['option_strict'] ? 1 : 0);
		data.append('option_partial2', state['option_partial2'] ? 1 : 0);
		data.append('option_partial3', state['option_partial3'] ? 1 : 0);
		data.append('option_unordered', state['option_unordered'] ? 1 : 0);
	}

	if (state['images']?.length > 0)
		state['images'].forEach((item) => {
			data.append('images[]', JSON.stringify({size: item?.size ?? 100, media_id: item.media_id}))
		});



	if (state['images_drawing']?.length > 0)
		state['images_drawing'].forEach((item) => {
			if(item?.media_id){
				data.append('images_drawing[]', JSON.stringify({size: item?.size ?? 100, media_id: item.media_id}))
			}
		});
	if (state['homeTaskFiles']?.length > 0)
		state['homeTaskFiles'].forEach((item) => {
			if((item.type === 'image-drawing' || item.type === 'image') && !!item.id) {
				data.append('update_images_size[]', JSON.stringify({ id: item.id, size: item.size ? item.size : state['image_size'] ? state['image_size'] : 100 }))
			}
		});
	if (state['audio']?.length > 0)
		state['audio'].forEach((item) => {
			if(item.media_id){
				data.append('audio[]', JSON.stringify({ media_id: item.media_id }))
			}
		});

	if (state['key_files']?.length > 0)
		state['key_files'].forEach((item) => {
			if(item.media_id){
				data.append('key_files[]', JSON.stringify({size: item?.size ?? 100, media_id: item.media_id}))
			}
		});

	return data;
};

export const useTask = (data, save, saveLS, cancelLS, removeLS) => {
	const addAlert = useAddAlert()
	const [loading, setLoading] = useState(false);
	const id = data?.id;

	const [state, setState] = useState({ ...getInitNewTask(data?.type), is_required: !id, ...data });
	const [errorsList, setErrorsList] = useState([]);
	const [showErrors, setShowErrors] = useState(false);

	const onSave = () => {
		const forProbe = isProbeEditor(state);
		const tableError = forProbe ? validateProbeTableTask(state) : '';
		if (tableError) {
			addAlert({ type: 'error', title: 'Ошибка сохранения задания', text: tableError });
			return;
		}
		if (
			state?.type === 'ai-tutor' &&
			!(state?.ai_tutor_id || state?.ai_tutor_id === 0)
		) {
			setShowErrors(true);
			addAlert({ type: 'error', title: "Ошибка", text: "Не выбран ИИ тьютор" });
			return;
		}

		if(
			state?.type === "drawing-image" && 
			(
				!state?.images_drawing?.[0]?.media_id && 
				(state?.homeTaskFiles?.filter(el => el.type === 'image-drawing')?.length === 0 || !state?.homeTaskFiles) 
			)
		) {
			addAlert({ type: 'error', title: "Ошибка сохранения задания!", text: "Не выбрано изображение для рисования" });
			errorListScroll(errorsList);
			return;
		}
		setShowErrors(true);
		if (!errorsList.valid) {
			addAlert({ type: 'error', title: "Ошибка сохранения задания!", text: "Не удалось сохранить задание — проверьте, что все поля заполнены правильно" })
			return errorListScroll(errorsList);
		}
		setLoading(true);
		
		const data = buildTaskFormData(state);

		// добавление нового
		if (!state?.id)
			return homeTaskApi
				.add({ data, expand: ['homeTaskFiles'] })
				.then((res) => {
					setLoading(false);

					if (res?.data?.data?.id) {
						if (forProbe && !hasSavedProbeTable(state, res.data.data)) {
							setState((prev) => ({ ...prev, id: res.data.data.id }));
							addAlert({ type: 'error', title: 'Формат ответа не сохранён', text: 'Сервис не сохранил настройки таблицы. Введённые данные оставлены в редакторе.' });
							return;
						}
						
						let modifiedRes = res?.data?.data;
						modifiedRes.devIdUnique = state?.devIdUnique;
						

						if (saveLS)
							saveLS({
								state: modifiedRes,
								devID: state.devID,
							});
						save();
					}
				});

		// обновление
		homeTaskApi
			.upd({ id: state.id, data, expand: ['homeTaskFiles'] })
			.then((res) => {
				setLoading(false);
				if (res.data?.status === 'success'){
					if (forProbe && !hasSavedProbeTable(state, res.data.data)) {
						addAlert({ type: 'error', title: 'Формат ответа не сохранён', text: 'Сервис не сохранил настройки таблицы. Введённые данные оставлены в редакторе.' });
						return;
					}
					// devID нужен только при повторной попытке привязать только что созданное задание.
					if (saveLS) saveLS({ state: res?.data?.data, ...(forProbe && !id && state.devID !== undefined ? { devID: state.devID } : {}) });
					else window.location.reload()
					save();
				}
			});
	};

	// удаление
	const onDel = () => {
		if (removeLS) return removeLS(data.id);
		homeTaskApi.remove(data.id).then((res) => {
			if (res.status === 204 || res.status === 200) {
				data.removeInListTask && data.removeInListTask(data.id)
			}
		});
	};

	const onCancel = () => {
		if (cancelLS) cancelLS(data.devID);
		save();
	};

	return [
		state,
		setState,
		onSave,
		onCancel,
		onDel,
		setErrorsList,
		showErrors,
		loading,
	];
};
